import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useMutation } from "react-query";
import api from "../../lib/api";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Button } from "@/components/ui/Button";
import { Combobox, userToComboboxOption } from "@/components/ui/Combobox";
import { ChevronLeft, Search } from "lucide-react";
import toast from "react-hot-toast";
import { Country, State } from "country-state-city";

const CreateSitePage = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: "",
    client: "",
    mine_type: "",
    address: "",
    phone: "",
    country: "",
    state: "",
    status: "operating",
    email: "",
    latitude: "",
    longitude: "",
    contact_name: "",
  });

  const [clients, setClients] = useState([]);
  const [mineTypes, setMineTypes] = useState([]);
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [selectedCountryName, setSelectedCountryName] = useState("");
  const [selectedStateName, setSelectedStateName] = useState("");
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);

  // Fetch countries, clients, and mine types on mount
  useEffect(() => {
    setCountries(Country.getAllCountries());

    const fetchClients = async () => {
      try {
        const res = await api.get("/clients/all-active-clients");
        let clientList = [];
        if (res.data?.data && Array.isArray(res.data.data)) {
          clientList = res.data.data;
        } else if (Array.isArray(res.data)) {
          clientList = res.data;
        }
        setClients(clientList);
      } catch (err) {
        toast.error("Failed to load clients");
        setClients([]);
      }
    };

    const fetchMineTypes = async () => {
      try {
        const res = await api.get("/mine-type?is_active=true&limit=100");
        if (res.data?.data && Array.isArray(res.data.data)) {
          setMineTypes(res.data.data);
        }
      } catch (err) {
        toast.error("Failed to load mine types");
        setMineTypes([]);
      }
    };

    fetchClients();
    fetchMineTypes();
  }, []);

  const handleCountryChange = (value) => {
    const selectedCountry = countries.find((country) => country.isoCode === value);
    const countryName = selectedCountry ? selectedCountry.name : "";

    setFormData((prev) => ({
      ...prev,
      country: countryName,
      state: "",
    }));
    setSelectedCountryName(countryName);
    setSelectedStateName("");

    const fetchedStates = State.getStatesOfCountry(value);
    setStates(fetchedStates);
  };

  const handleStateChange = (value) => {
    const selectedState = states.find((state) => state.isoCode === value);
    const stateName = selectedState ? selectedState.name : "";

    setFormData((prev) => ({
      ...prev,
      state: stateName,
    }));
    setSelectedStateName(stateName);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    if (name === "client") {
      // Find the selected client to get their display name
      const selectedClient = clients.find((client) => client.id === value);
      const displayName = selectedClient
        ? selectedClient.first_name && selectedClient.last_name
          ? `${selectedClient.first_name} ${selectedClient.last_name}`
          : selectedClient.name || selectedClient.email
        : value;

      setFormData((prev) => ({ ...prev, [name]: value }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const checkDuplicateSiteName = async (siteName, clientId) => {
    if (!siteName || !siteName.trim() || !clientId) return false;

    try {
      const response = await api.get(
        `/sites/check-duplicate-name?site_name=${encodeURIComponent(siteName.trim())}&client_id=${clientId}`
      );
      return response.data.exists;
    } catch (error) {
      return false;
    }
  };

  const createMutation = useMutation((siteData) => api.post("/sites", siteData), {
    onSuccess: () => {
      toast.success("Site created successfully");
      navigate("/site");
    },
    onError: (error) => {
      toast.error(error.response?.data?.message || "Failed to create site");
    },
  });

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check if all required fields are empty
    const allFieldsEmpty =
      !formData.name.trim() &&
      !formData.client &&
      !formData.address.trim() &&
      !formData.email.trim() &&
      !formData.mine_type.trim();

    if (allFieldsEmpty) {
      toast.error("Please fill in the data before adding");
      return;
    }

    // Check individual fields and show specific messages
    let hasErrors = false;

    if (!formData.name.trim()) {
      toast.error("Site name is required");
      hasErrors = true;
    }

    if (!formData.client) {
      toast.error("Please select Client");
      hasErrors = true;
    }

    if (!formData.mine_type.trim()) {
      toast.error("please select Mine Type");
      hasErrors = true;
    }

    if (!formData.address.trim()) {
      toast.error("Address is required");
      hasErrors = true;
    }

    if (!formData.email.trim()) {
      toast.error("Email is required");
      hasErrors = true;
    }

    if (!formData.country.trim()) {
      toast.error("Country is required");
      hasErrors = true;
    }

    // If there are validation errors, don't submit
    if (hasErrors) {
      return;
    }

    setIsCheckingDuplicate(true);
    try {
      const isDuplicateName = await checkDuplicateSiteName(formData.name, formData.client);
      if (isDuplicateName) {
        toast.error(
          "A site with this name already exists for this client. Please choose a different site name."
        );
        setIsCheckingDuplicate(false);
        return;
      }
    } catch (error) {
      toast.error("Error checking for duplicate site names. Please try again.");
      setIsCheckingDuplicate(false);
      return;
    }
    setIsCheckingDuplicate(false);

    const submitData = {
      name: formData.name.trim(),
      client: formData.client,
      mine_type: formData.mine_type || null,
      address: formData.address,
      phone: formData.phone.trim() || null,
      country: formData.country || null,
      state: formData.state || null,
      status: formData.status,
      email: formData.email,
      contact_name: formData.contact_name,
      latitude:
        formData.latitude && formData.latitude !== "1" ? parseFloat(formData.latitude) : null,
      longitude:
        formData.longitude && formData.longitude !== "1" ? parseFloat(formData.longitude) : null,
    };

    createMutation.mutate(submitData);
  };

  return (
    <div className="">
      <div className="w-full">
        <div className="slide-up">
          <form onSubmit={handleSubmit} noValidate className="">
            {/* Site, Client, Mine Type */}
            <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-3">
              <div className="min-w-0 space-y-3">
                <Label htmlFor="name" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Site *
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  placeholder="Site Name"
                  value={formData.name}
                  onChange={handleChange}
                />
              </div>
              <div className="min-w-0 space-y-3">
                <Label htmlFor="client" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Client *
                </Label>
                <Combobox
                  options={clients.map(userToComboboxOption)}
                  value={formData.client}
                  onValueChange={(value) => handleSelectChange("client", value)}
                  placeholder="SELECT CLIENT"
                  searchPlaceholder="Search clients..."
                />
              </div>
              <div className="min-w-0 space-y-3">
                <Label
                  htmlFor="mine_type"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Mine Type *
                </Label>
                <Combobox
                  options={mineTypes.map((mineType) => ({
                    value: mineType.name,
                    label: mineType.name,
                  }))}
                  value={formData.mine_type}
                  onValueChange={(value) => handleSelectChange("mine_type", value)}
                  placeholder="MINE TYPE"
                  searchPlaceholder="Search mine types..."
                />
              </div>
            </div>
            {/* Address, Email, Phone */}
            <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-3">
              <div className="min-w-0 space-y-3">
                <Label
                  htmlFor="contact_name"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Site Contact:
                </Label>
                <Input
                  id="contact_name"
                  name="contact_name"
                  type="text"
                  placeholder="john doe"
                  value={formData.contact_name}
                  onChange={handleChange}
                />
              </div>

              <div className="min-w-0 space-y-3">
                <Label htmlFor="email" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Email *
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="contact@site.com"
                  value={formData.email}
                  onChange={handleChange}
                />
              </div>
              <div className="min-w-0 space-y-3">
                <Label htmlFor="phone" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Phone No:
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+91-9876543210"
                  value={formData.phone}
                  onChange={handleChange}
                />
              </div>
            </div>

            {/* Country, State, Status */}
            <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-3">
              <div className="min-w-0 space-y-3">
                <Label htmlFor="country" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Country *
                </Label>
                <Combobox
                  options={countries.map((country) => ({
                    value: country.isoCode,
                    label: country.name,
                  }))}
                  value={countries.find((c) => c.name === selectedCountryName)?.isoCode || ""}
                  onValueChange={handleCountryChange}
                  placeholder="COUNTRY"
                  searchPlaceholder="Search countries..."
                />
              </div>
              <div className="min-w-0 space-y-3">
                <Label htmlFor="state" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  State:
                </Label>
                <Combobox
                  options={states.map((state) => ({
                    value: state.isoCode,
                    label: state.name,
                  }))}
                  value={states.find((s) => s.name === selectedStateName)?.isoCode || ""}
                  onValueChange={handleStateChange}
                  placeholder={selectedCountryName ? "STATE" : "SELECT COUNTRY FIRST"}
                  searchPlaceholder="Search states..."
                  disabled={!states.length}
                />
              </div>
              <div className="min-w-0 space-y-3">
                <Label htmlFor="status" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Status:
                </Label>
                <Combobox
                  options={[
                    { value: "operating", label: "Operating" },
                    { value: "care_maintenance", label: "Care & Maintenance" },
                    { value: "developing", label: "Developing" },
                  ]}
                  value={formData.status}
                  onValueChange={(value) => handleSelectChange("status", value)}
                  placeholder="STATUS"
                  searchPlaceholder="Search status..."
                />
              </div>
            </div>

            {/* Latitude and Longitude */}
            <div className="grid grid-cols-1 gap-4 mb-8 sm:grid-cols-2 lg:grid-cols-3">
              <div className="min-w-0 space-y-3">
                <Label htmlFor="address" className="pl-3 text-xs font-medium uppercase text-gray-0">
                  Address *
                </Label>
                <Input
                  id="address"
                  name="address"
                  type="text"
                  placeholder="123 Mining Road"
                  value={formData.address}
                  onChange={handleChange}
                />
              </div>
              <div className="min-w-0 space-y-3">
                <Label
                  htmlFor="latitude"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Latitude:
                </Label>
                <Input
                  id="latitude"
                  name="latitude"
                  type="number"
                  placeholder="40.7128"
                  value={formData.latitude}
                  onChange={handleChange}
                />
              </div>
              <div className="min-w-0 space-y-3">
                <Label
                  htmlFor="longitude"
                  className="pl-3 text-xs font-medium uppercase text-gray-0"
                >
                  Longitude:
                </Label>
                <Input
                  id="longitude"
                  name="longitude"
                  type="number"
                  placeholder="-74.0060"
                  value={formData.longitude}
                  onChange={handleChange}
                />
              </div>

            </div>

            {/* Submit Buttons */}
            <div className="flex justify-end border-t border-b border-gray-0">
              <div className="flex items-center justify-center w-auto">
                <Link className="border-r rounded-b-none border-gray-0" to="/site">
                  <Button
                    variant="secondary"
                    className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal uppercase rounded-none text-gray-0 hover:scale-100"
                  >
                    <ChevronLeft className="w-4 h-4 mr-2 text-red-500" />
                    Back
                  </Button>
                </Link>
                <Button
                  className="flex items-center justify-center h-auto px-10 py-5 text-xs font-normal text-red-500 uppercase bg-transparent border-0 rounded-none rounded-b-none hover:scale-100"
                  type="submit"
                  disabled={createMutation.isLoading || isCheckingDuplicate}
                >
                  {isCheckingDuplicate
                    ? "Checking..."
                    : createMutation.isLoading
                      ? "Creating..."
                      : "Save"}
                </Button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateSitePage;
